import { formatNumber } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { combineLatest, concat, EMPTY, Observable, of, throwError } from 'rxjs';
import {
    catchError,
    concatMap,
    delay,
    distinctUntilChanged,
    filter,
    finalize,
    last,
    map,
    mergeMap,
    scan,
    startWith,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs/operators';
import { selectEditingTreatments } from 'src/app/advice/store/treatment.selectors';
import { sortTreatments } from 'src/app/shared/util/sort-treatments';
import { CropAid } from 'src/app/crop-aid/crop-aid.model';
import { selectCropAids, selectOrganicCropAids } from 'src/app/crop-aid/store/crop-aid.selectors';
import { triggerDelayedGetFertilizationLegalStandards } from 'src/app/legal-standard/store/legal-standard.actions';
import {
    SalesOrderPeriod,
    TreatmentType,
} from 'src/app/sales-order-period/models/sales-order-period.models';
import {
    selectPastSalesOrderPeriods,
    selectPastSalesOrderPeriodsByType,
    selectSalesOrderPeriodsByType,
} from 'src/app/sales-order-period/store/sales-order-period.selectors';
import {
    CropData,
    CropFieldAdvice,
    ExecutedTreatmentWithNitrogenEffectivity,
    FertilizationBalanceSettingsResponse,
    FertilizationSettingWithIds,
    FertilizationSettingWithName,
    OrganicMatterSupply,
    SiteState,
    SoilAnalysis,
    Template,
    Treatment,
    TreatmentOrExecutedTreatment,
    TreatmentWithNitrogenEffectivity,
} from 'src/app/shared/models';
import { ApiService } from 'src/app/shared/services/api.service';
import { ArableCustomerService } from 'src/app/shared/services/arable-customer.service';
import {
    AppActionTypes,
    GetCompanySettings,
    GetCropFieldAdvices,
    GetOrganicMatterReport,
    UpdateGrasslandUsages,
} from 'src/app/store/actions';
import {
    getCropYear,
    selectAvailableCropFieldAdvices,
    selectCompanyGrasslandUsage,
    selectCompanySettings,
    selectCropFieldAdvices,
    selectCrops,
    selectGreenManureCrops,
    selectNonProductiveCrops,
} from 'src/app/store/selectors';

import { Environment } from 'src/environments/environment';

import {
    AddTreatment,
    AdviceActionTypes,
    ApplyPreviousYearsAdvice,
    ChangeExecutedTreatmentType,
    ClearAdviceConfirmed,
    ClearAdviceConfirmedSuccess,
    CopyExecutedTreatmentsToOtherAdvices,
    CopyExecutedTreatmentToOtherAdvicesDialogs,
    CopyTreatment,
    CopyTreatments,
    CopyTreatmentsSuccess,
    CopyTreatmentsTo,
    CopyTreatmentsToOtherAdvices,
    CopyTreatmentToOtherAdvicesDialogs,
    DoneEditingFertilizationBalanceSettings,
    EditExecutedTreatment,
    EditTreatment,
    GetAdvicesForCustomer,
    GetExecutedTreatmentForAdviceId,
    GetFertilizationSetting,
    GetLatestSoilAnalysis,
    GetTemplate,
    ImportAdviceFromCustomer,
    LoadEditingAdvice,
    LoadNitrogenCalculationSettingToggles,
    LoadNitrogenCalculationSettingTogglesByAdvices,
    OpenCopyTreatmentsToDialog,
    OpenEditAllFertilizationSettingsDialog,
    OpenEditFertilizationBalanceSettingsDialog,
    OpenFertilizationSettingDialog,
    OpenSelectSchemaDialog,
    RemoveExecutedTreatment,
    RemoveExecutedTreatmentRequest,
    RemoveExecutedTreatmentSuccess,
    RemoveTreatment,
    RemoveTreatmentRequest,
    RemoveTreatmentSuccess,
    SaveExecutedTreatment,
    SaveExecutedTreatmentSuccess,
    SaveFertilizationSetting,
    SaveFertilizationSettingSuccess,
    SaveTreatment,
    SaveTreatments,
    SaveTreatmentsSuccess,
    SaveTreatmentSuccess,
    SelectAdviceForImport,
    SelectPreviousYearsAdvice,
    SelectSchemaSilently,
    SetCustomerFertilizerStock,
    SetEditingAdvice,
    SetExecutedTreatments,
    SetFertilizationSetting,
    SetFertilizationSettings,
    SetLatestSoilAnalysis,
    SetNitrogenCalculationSettingToggles,
    SetNitrogenCalculationSettingTogglesByAdvices,
    SetRecommendedNutrientQuantityByAdviceIds,
    SetTemplate,
    ToggleAdviceCompleted,
    TriggerEditFertilizationBalanceSettingsDialog,
    UpdateCompanySettings,
    UpdatedTreatments,
    UpdateExpectedCropYieldFailure,
    UpdateExpectedCropYieldSuccess,
} from './actions';
import {
    selectCropFieldAdviceById,
    selectEditingAdvice,
    selectEditingCropFieldAdviceId,
    selectEditingTreatmentType,
    selectExecutedTreatments,
    selectFertilizationSetting,
    selectFertilizationSettings,
    selectTemplate,
} from './selectors';
import * as CopyAdviceActions from '../../copy-advice/store/copy-advice.actions';
import * as LegalRestrictionsActions from '../../legal-restriction/store/legal-restriction.actions';
import {
    SelectCropFieldsDialogComponent,
    SelectCropFieldsDialogData,
} from '../../shared/components/select-cropfields-dialog/select-cropfields-dialog.component';
import { CompanySettingsDialogComponent } from '../components/company-settings-dialog/company-settings-dialog.component';
import {
    CopyAdviceConfirmationDialogComponent,
    CopyAdviceConfirmationDialogOutputModel,
} from '../components/copy-advice-confirmation-dialog/copy-advice-confirmation-dialog.component';
// eslint-disable-next-line max-len
import { EditAllFertilizationSettingsDialogComponent } from '../components/edit-all-fertilization-settings-dialog/edit-all-fertilization-settings-dialog.component';
// eslint-disable-next-line max-len
import { EditFertilizationBalanceSettingsDialogComponent } from '../components/edit-fertilization-balance-settings-dialog/edit-fertilization-balance-settings-dialog.component';
import { FertilizationSettingDialogComponent } from '../components/fertilization-setting-dialog/fertilization-setting-dialog.component';
import { SelectCustomerDialogComponent } from '../components/select-customer-dialog/select-customer-dialog.component';
import { TreatmentDialogComponent } from '../components/treatment-dialog/treatment-dialog.component';

@Injectable()
export class AdviceEffects {
  protected readonly dialog = inject(DialogService);
  protected readonly store = inject<Store<SiteState>>(Store);
  protected readonly actions$ = inject(Actions);
  protected readonly api = inject(ApiService);
  protected readonly router = inject(Router);
  private readonly arableCustomerService = inject(ArableCustomerService);
  private readonly locale = inject(LocaleService);
  private readonly env = inject<Environment>(EnvironmentInjector);

  routerNavigated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      filter(({ payload }: RouterNavigatedAction) => payload.event.url.indexOf('/advice') === 0),
      switchMap((_) => of(new LoadEditingAdvice())),
    ),
  );

  setCropFieldAdvices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SetCropFieldAdvices),
      switchMap((_) => of(new LoadEditingAdvice())),
    ),
  );

  loadEditingAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.LoadEditingAdvice),
      withLatestFrom(
        this.store.pipe(select(selectEditingCropFieldAdviceId)),
        this.store.pipe(select(selectEditingTreatmentType)),
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(selectCropFieldAdvices)),
      ),
      filter(([_, adviceId, treatmentType, cropYear]) => !!(adviceId && treatmentType && cropYear)),
      distinctUntilChanged(
        (
          [_, currentAdviceId, currentTreatmentType, currentCropYear],
          [__, nextAdviceId, nextTreatmentType, nextCropYear],
        ) =>
          currentAdviceId === nextAdviceId &&
          currentTreatmentType === nextTreatmentType &&
          currentCropYear === nextCropYear,
      ),
      switchMap(([_, adviceId, treatmentType, cropYear, cropFieldAdvices]) =>
        this.store.pipe(
          select(selectCropFieldAdviceById(adviceId)),
          filter((cropFieldAdvice) => cropFieldAdvice && cropFieldAdvice.cropYear === cropYear),
          take(1),
          switchMap((cropFieldAdvice) => {
            const actions = [
              new GetTemplate(cropYear, cropFieldAdvice, treatmentType as TreatmentType),
              ...(treatmentType === TreatmentType.Fertilization
                ? [
                    new GetExecutedTreatmentForAdviceId(cropFieldAdvice.adviceId),
                    new LoadNitrogenCalculationSettingToggles(cropFieldAdvice.adviceId),
                  ]
                : []),
              new SetEditingAdvice(),
              new GetLatestSoilAnalysis(cropFieldAdvice.soilAnalysisIds),
              new GetOrganicMatterReport(cropFieldAdvices.map((c) => c.cropFieldId)),
            ];
            return of(...actions);
          }),
        ),
      ),
    ),
  );

  getExecutedTreatments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.GetExecutedTreatmentForAdviceId),
      switchMap(({ adviceId }: GetExecutedTreatmentForAdviceId) =>
        this.api.getExecutedCropFieldTreatmentsByAdviceId(adviceId).pipe(
          withLatestFrom(this.store.pipe(select(selectPastSalesOrderPeriods))),
          map(
            ([executedTreatments, pastSalesOrderPeriods]) =>
              new SetExecutedTreatments(executedTreatments, pastSalesOrderPeriods),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.GetTemplate),
      switchMap(({ cropYear, cropFieldAdvice, treatmentType }: GetTemplate) =>
        this.api.getTemplate(cropYear, cropFieldAdvice, treatmentType).pipe(
          catchError((error: HttpErrorResponse) =>
            error.status === 404
              ? of({
                  treatments: [],
                  treatmentSchemas: [],
                } as Template)
              : throwError(error),
          ),
          concatMap((template) => {
            const treatments = sortTreatments(template.treatments);
            const treatmentOrder = new Map(treatments.map(({ id }, index) => [id, index]));
            const orderedTemplate: Template = {
              ...template,
              treatments,
              treatmentSchemas: template.treatmentSchemas.map((schema) => ({
                ...schema,
                treatmentIds: [...schema.treatmentIds].sort(
                  (a, b) =>
                    (treatmentOrder.get(a) ?? Number.MAX_SAFE_INTEGER) -
                    (treatmentOrder.get(b) ?? Number.MAX_SAFE_INTEGER),
                ),
              })),
            };

            if (
              treatmentType === TreatmentType.Fertilization &&
              this.router.url.indexOf('report') === -1
            ) {
              return [
                new SetTemplate(orderedTemplate),
                new GetFertilizationSetting(cropFieldAdvice.adviceId),
              ];
            }
            return [new SetTemplate(orderedTemplate), new SelectSchemaSilently()];
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  selectSchemaSilently$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.SelectSchemaSilently),
      withLatestFrom(
        this.store.pipe(select(selectTemplate)),
        this.store.pipe(select(selectEditingTreatments)),
      ),
      switchMap(([_, template, treatments]) =>
        template?.treatmentSchemas.length > 0 && treatments.length === 0
          ? of(new OpenSelectSchemaDialog())
          : EMPTY,
      ),
    ),
  );

  openSelectSchemaDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.OpenSelectSchemaDialog),
      withLatestFrom(
        this.store.pipe(select(selectTemplate)),
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectEditingTreatmentType)),
      ),
      concatMap(([_, template, cropFieldAdvice, treatmentType]) => {
        if (
          (treatmentType === TreatmentType.Fertilization && cropFieldAdvice.fertilizationCorrupt) ||
          (treatmentType === TreatmentType.GBM && cropFieldAdvice.gbmCorrupt)
        ) {
          return this.dialog
            .notify({ text: this.locale.translate('invalidTreatments') })
            .pipe(switchMap(() => EMPTY));
        }

        return this.dialog
          .multiselect({
            options: (template?.treatmentSchemas ? template.treatmentSchemas : [])
              .map((treatmentSchema) => ({
                name: `${treatmentSchema.name} ${
                  treatmentSchema.isCorrupt ? '(' + this.locale.translate('corrupt') + ')' : ''
                }`,
                description: treatmentSchema.description,
                value: treatmentSchema,
              }))
              .sort((a, b) => a.name.localeCompare(b.name)),
            title: this.locale.translate('selectSchema'),
            selected: null,
            emptyText: this.locale.translate('noSchemas'),
            searchBox: true,
            searchBoxSticky: true,
          })
          .pipe(
            concatMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm && result && result.length > 0) {
                const treatmentIds = result
                  .map((schema) => schema.treatmentIds)
                  .reduce((a, b) => a.concat(b), []);
                const corruptSchemas = result.map((schema) => schema.isCorrupt);
                const hasCorruptSchemas = corruptSchemas.indexOf(true) !== -1;

                if (hasCorruptSchemas) {
                  return this.dialog
                    .notify({
                      title: this.locale.translate('corruptSchedulesSelected'),
                      text: this.locale.translate('makeASelectionWithoutCorruptSchemes'),
                    })
                    .pipe(switchMap(() => of(new OpenSelectSchemaDialog())));
                }

                const treatments = sortTreatments(
                  treatmentIds
                    .map((id) => template.treatments.find((treatment) => treatment.id === id))
                    .filter((treatment): treatment is Treatment => treatment !== undefined),
                );
                return of(
                  new SaveTreatments(cropFieldAdvice.adviceId, [
                    ...treatments.map((treatment) => ({
                      ...treatment,
                      id: '',
                      areaInHectaresManual: cropFieldAdvice.areaInHectares,
                      driftReduction:
                        treatment.treatmentType === TreatmentType.GBM
                          ? (treatment.driftReduction ?? 75)
                          : null,
                    })),
                  ]),
                );
              }
              return EMPTY;
            }),
          );
      }),
    ),
  );

  addTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.AddTreatment),
      withLatestFrom(
        this.store.pipe(select(selectTemplate)),
        this.store.pipe(select(selectCropAids)),
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectEditingTreatments)),
      ),
      concatMap(
        ([{ treatmentType }, template, cropAids, cropFieldAdvice, editingTreatments]: [
          AddTreatment,
          Template,
          CropAid[],
          CropFieldAdvice,
          Treatment[],
        ]) => {
          if (
            (treatmentType === TreatmentType.Fertilization &&
              cropFieldAdvice.fertilizationCorrupt) ||
            (treatmentType === TreatmentType.GBM && cropFieldAdvice.gbmCorrupt)
          ) {
            return this.dialog
              .notify({ text: this.locale.translate('invalidTreatments') })
              .pipe(switchMap((_) => EMPTY));
          }

          if (!template?.treatments?.length) {
            return of(new EditTreatment(treatmentType));
          }

          return this.dialog
            .multiselect({
              options: template.treatments.map((treatment) => ({
                name: this.getAddTreatmentName(treatment, editingTreatments),
                value: treatment,
                description: `${this.locale.translate(treatment.objective)}; ${treatment.aidAdvices
                  .map((aidAdvice) => {
                    const cropAid = cropAids.find((item) => item.id === aidAdvice.cropAidId);
                    if (cropAid) {
                      return `${cropAid.name} ${aidAdvice.dosage} ${cropAid.unitOfMeasurement}`;
                    }
                    return '';
                  })
                  .join(', ')}`,
              })),
              title: this.locale.translate('selectTreatments'),
              altButton: true,
              altButtonColor: 'default',
              altButtonIcon: 'add',
              altButtonText: this.locale.translate('defineTreatment'),
              selected: [],
              emptyText: '',
              searchBox: true,
              searchBoxSticky: true,
            })
            .pipe(
              concatMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.alt) {
                  return of(new EditTreatment(treatmentType));
                } else if (status === DIALOG_RETURN_TYPES.confirm) {
                  if (result.length === 0) {
                    return EMPTY;
                  }

                  const invalidTreatment = result.find(
                    (treatment) =>
                      !treatment.aidAdvices.every(
                        (aidAdvice) =>
                          cropAids.find((cropAid) => cropAid.id === aidAdvice.cropAidId)?.isActive,
                      ),
                  );
                  if (invalidTreatment) {
                    const message = this.buildInvalidTreatmentsMessage(invalidTreatment, cropAids);

                    return this.dialog.notify({ text: message }).pipe(switchMap((_) => EMPTY));
                  } else {
                    return of(
                      new SaveTreatments(cropFieldAdvice.adviceId, [
                        ...result.map((treatment) => ({
                          ...treatment,
                          id: '',
                          areaInHectaresManual: cropFieldAdvice.areaInHectares,
                          driftReduction:
                            treatmentType === TreatmentType.GBM
                              ? (treatment.driftReduction ?? 75)
                              : null,
                        })),
                      ]),
                    );
                  }
                } else {
                  return EMPTY;
                }
              }),
            );
        },
      ),
    ),
  );

  editTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.EditTreatment),
      switchMap(({ treatment, treatmentType, isAnExecutedTreatment }: EditTreatment) =>
        combineLatest([
          this.store.pipe(select(selectCropAids)),
          this.store.pipe(select(selectOrganicCropAids)),
          this.store.pipe(select(selectEditingAdvice)),
          this.store.pipe(select(selectSalesOrderPeriodsByType({ periodType: treatmentType }))),
          this.store.pipe(select(selectPastSalesOrderPeriodsByType({ periodType: treatmentType }))),
        ]).pipe(
          take(1),
          concatMap(
            ([
              cropAids,
              organicCropAids,
              cropFieldAdvice,
              salesOrderPeriods,
              pastSalesOrderPeriods,
            ]: [CropAid[], CropAid[], CropFieldAdvice, SalesOrderPeriod[], SalesOrderPeriod[]]) => {
              const fertilizationObjectives = [
                { value: 'basegift', name: this.locale.translate('basegift') },
                { value: 'extraFertilization', name: this.locale.translate('extraFertilization') },
                { value: 'leafFertilization', name: this.locale.translate('leafFertilization') },
                { value: 'fertigation', name: this.locale.translate('fertigation') },
              ];
              const gbmObjectives = [
                { value: 'weed', name: this.locale.translate('weed') },
                { value: 'disease', name: this.locale.translate('disease') },
                { value: 'pest', name: this.locale.translate('pest') },
                { value: 'foliarFertilizer', name: this.locale.translate('foliarFertilizer') },
                { value: 'growthRegulation', name: this.locale.translate('growthRegulation') },
                { value: 'haulmStripping', name: this.locale.translate('haulmStripping') },
                { value: 'productTreatment', name: this.locale.translate('productTreatment') },
                { value: 'other', name: this.locale.translate('other') },
              ];
              const cropAndCropAids = treatment.aidAdvices.map(({ cropAidId }) => ({
                cropId: cropFieldAdvice.crop.id,
                cropAidId,
              }));

              return this.dialog
                .form(
                  TreatmentDialogComponent,
                  {
                    title: this.locale.translate(`${treatmentType}TreatmentUppercase`),
                    treatment: {
                      ...treatment,
                      treatmentType,
                      aidAdvices: treatment.aidAdvices.map((aidAdvice) => ({ ...aidAdvice })),
                    },
                    cropAids:
                      treatmentType === TreatmentType.GBM
                        ? cropAids.filter((cropAid) => cropAid.isCropProtection)
                        : cropFieldAdvice.isSkal
                          ? organicCropAids
                          : cropAids.filter((cropAid) => cropAid.isFertilizer),
                    salesOrderPeriods,
                    pastSalesOrderPeriods,
                    areaInHectares: cropFieldAdvice.areaInHectares,
                    treatmentType,
                    objectives:
                      treatmentType === TreatmentType.Fertilization
                        ? fertilizationObjectives
                        : gbmObjectives,
                    isAnExecutedTreatment,
                    cropId: cropFieldAdvice.crop.id,
                    adviceId: cropFieldAdvice.adviceId,
                    isPlanetProof: cropFieldAdvice.isPlanetProof,
                    province: cropFieldAdvice.province,
                    cropfieldId: cropFieldAdvice.cropFieldId,
                  },
                  {
                    minWidth: '60vw',
                  },
                )
                .pipe(
                  switchMap(({ status, result }) => {
                    if (status !== DIALOG_RETURN_TYPES.confirm) {
                      return EMPTY;
                    }

                    if (
                      !(result.id === 0 || result.id === '') &&
                      result.isAnExecutedTreatment !== treatment.isAnExecutedTreatment
                    ) {
                      return [new ChangeExecutedTreatmentType(result, cropFieldAdvice.adviceId)];
                    }

                    if (result.isAnExecutedTreatment) {
                      return [
                        new SaveExecutedTreatment({
                          ...(result as ExecutedTreatmentWithNitrogenEffectivity),
                          adviceId: cropFieldAdvice.adviceId,
                        }),
                      ];
                    }

                    return [
                      new SaveTreatment(
                        cropFieldAdvice.adviceId,
                        result as TreatmentWithNitrogenEffectivity,
                      ),
                    ];
                  }),
                  startWith(LegalRestrictionsActions.getLegalRestrictions({ cropAndCropAids })),
                );
            },
          ),
        ),
      ),
    ),
  );

  changeExecutedTreamentType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.ChangeExecutedTreatmentType),
      concatMap(({ treatment, adviceId }: ChangeExecutedTreatmentType) => {
        if (treatment.isAnExecutedTreatment) {
          return [
            new RemoveTreatmentRequest(adviceId, treatment.id as string),
            new SaveExecutedTreatment({
              ...(treatment as ExecutedTreatmentWithNitrogenEffectivity),
              id: 0,
              adviceId,
            }),
          ];
        } else {
          return [
            new RemoveExecutedTreatmentRequest(treatment.id as number),
            new SaveTreatment(adviceId, {
              ...(treatment as TreatmentWithNitrogenEffectivity),
              id: '',
            }),
          ];
        }
      }),
    ),
  );

  editExecutedTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.EditExecutedTreatment),
      map(
        ({ treatment, treatmentType }: EditExecutedTreatment) =>
          new EditTreatment(treatmentType, true, treatment),
      ),
    ),
  );

  saveExecutedTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.SaveExecutedTreatment),
      switchMap(({ executedTreatment }: SaveExecutedTreatment) =>
        this.api.upsertExecutedTreatment(executedTreatment).pipe(
          concatMap(() => [new SaveExecutedTreatmentSuccess(), new UpdatedTreatments()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  removeExecutedTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.RemoveExecutedTreatment),
      switchMap(({ id }: RemoveExecutedTreatment) =>
        this.dialog
          .confirm({
            title: this.locale.translate('clearExecutedTreatmentTitle'),
            text: this.locale.translate('clearExecutedTreatmentText'),
          })
          .pipe(
            switchMap(({ status }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? of(new RemoveExecutedTreatmentRequest(id))
                : EMPTY,
            ),
          ),
      ),
    ),
  );

  removeExecutedTreatmentRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.RemoveExecutedTreatmentRequest),
      switchMap(({ id }: RemoveExecutedTreatmentRequest) =>
        this.api.removeExecutedTreatment(id).pipe(
          concatMap(() => [new RemoveExecutedTreatmentSuccess(), new UpdatedTreatments()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getPreviousYearsAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.GetPreviousYearsAdvice),
      withLatestFrom(this.store.pipe(select(selectEditingAdvice))),
      switchMap(([_, cropFieldAdvice]: [never, CropFieldAdvice]) => {
        const previousYear = cropFieldAdvice.cropYear - 1;

        return this.api.getCropFieldAdvicesByCropYear(previousYear).pipe(
          map((advices) => new SelectPreviousYearsAdvice(advices)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  selectPreviousYearsAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.SelectPreviousYearsAdvice),
      withLatestFrom(this.store.pipe(select(selectEditingAdvice))),
      concatMap(
        ([{ cropFieldAdvices }, cropFieldAdvice]: [SelectPreviousYearsAdvice, CropFieldAdvice]) => {
          cropFieldAdvices = cropFieldAdvices.filter(
            (advice) => cropFieldAdvice && advice.crop.id === cropFieldAdvice.crop.id,
          );

          if (cropFieldAdvices.length === 0) {
            return this.dialog
              .notify({
                text: this.locale.translate('noAdvicesFound'),
              })
              .pipe(switchMap((__) => EMPTY));
          }

          if (cropFieldAdvices.length === 1) {
            if (cropFieldAdvices[0].fertilizationCorrupt === true) {
              return this.dialog
                .notify({
                  text: this.locale.translate('advicePreviousYearInvalid'),
                })
                .pipe(switchMap((__) => EMPTY));
            }
            return of(new ApplyPreviousYearsAdvice(cropFieldAdvices[0], cropFieldAdvice));
          }

          return this.dialog
            .select({
              options: cropFieldAdvices.map((advice) => {
                const descriptionItems = [
                  { label: 'crop', value: advice.crop },
                  { label: 'cropVariety', value: advice.cropVariety },
                  { label: 'soilType', value: advice.soilType },
                ];
                let description = '';
                descriptionItems.forEach((item) => {
                  if (item.value && item.value.name) {
                    description = `${description} ${this.locale.translate(item.label)}: ${
                      item.value.name
                    },`;
                  }
                });
                description = description.slice(0, description.length - 1);
                return {
                  name: `${advice.name}${advice.fertilizationCorrupt ? ' (Ongeldig)' : ''}`,
                  value: advice,
                  description,
                  disabled: advice.fertilizationCorrupt,
                };
              }),
              selected: null,
              emptyText: this.locale.translate('noAdvicesFound'),
            })
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.confirm) {
                  if (result.fertilizationCorrupt) {
                    return this.dialog
                      .notify({
                        text: this.locale.translate('invalidAdviceApplied'),
                      })
                      .pipe(switchMap((__) => EMPTY));
                  }
                  return of(new ApplyPreviousYearsAdvice(result, cropFieldAdvice));
                }
                return EMPTY;
              }),
            );
        },
      ),
    ),
  );

  applyPreviousYearsAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.ApplyPreviousYearsAdvice),
      map(
        ({ sourceCropFieldAdvice, targetCropFieldAdvice }) =>
          new CopyTreatments(
            sourceCropFieldAdvice,
            [targetCropFieldAdvice],
            TreatmentType.Fertilization,
            false,
          ),
      ),
    ),
  );

  copyTreatmentsTo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.CopyTreatmentsTo),
      withLatestFrom(
        this.store.pipe(select(selectEditingTreatments)),
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectExecutedTreatments)),
      ),
      concatMap(
        ([{ treatmentType }, treatments, sourceCropFieldAdvice, executedTreatments]: [
          CopyTreatmentsTo,
          TreatmentWithNitrogenEffectivity[],
          CropFieldAdvice,
          ExecutedTreatmentWithNitrogenEffectivity[],
        ]) => {
          if (!treatments.length) {
            return this.dialog
              .notify({
                text: this.locale.translate('copyAdviceToEmptyText'),
              })
              .pipe(switchMap((_) => EMPTY));
          }

          if (
            (treatmentType === TreatmentType.Fertilization &&
              sourceCropFieldAdvice.fertilizationCorrupt) ||
            (treatmentType === TreatmentType.GBM && sourceCropFieldAdvice.gbmCorrupt)
          ) {
            return this.dialog
              .notify({ text: this.locale.translate('invalidTreatments') })
              .pipe(switchMap((_) => EMPTY));
          }

          if (treatmentType === TreatmentType.Fertilization && executedTreatments.length > 0) {
            return this.dialog
              .confirm({
                title: this.locale.translate('copyExecutedTreatments'),
                confirmButtonText: this.locale.translate('yes'),
                altButton: true,
                altButtonText: this.locale.translate('cancel'),
                cancelButtonText: this.locale.translate('no'),
              })
              .pipe(
                concatMap(({ status }) =>
                  status === DIALOG_RETURN_TYPES.alt
                    ? EMPTY
                    : of(
                        new OpenCopyTreatmentsToDialog(
                          status === DIALOG_RETURN_TYPES.confirm,
                          treatmentType,
                        ),
                      ),
                ),
              );
          } else {
            return of(new OpenCopyTreatmentsToDialog(false, treatmentType));
          }
        },
      ),
    ),
  );

  /**
   * Effect for handling the copying of advice treatments to another crop field.
   * Flow contains a couple of steps: selecting crop field(s), asking for confirmation
   * whether the user wants to copy non-GWB advices to a GWB crop field, checking whether
   * the source & target advice are the same crop type and asking for confirmation about
   * the copy action that will be started.
   */
  openCopyTreatmentsToDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.OpenCopyTreatmentsToDialog),
      withLatestFrom(
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectAvailableCropFieldAdvices())),
        this.store.pipe(select(selectCrops)),
      ),
      concatMap(
        ([
          { treatmentType, copyExecutedTreatments },
          sourceCropFieldAdvice,
          targetCropFieldAdvices,
          crops,
        ]: [OpenCopyTreatmentsToDialog, CropFieldAdvice, CropFieldAdvice[], CropData[]]) =>
          this.copyTreatmentsSelectCropFieldsDialog(
            sourceCropFieldAdvice,
            targetCropFieldAdvices,
            treatmentType,
            crops,
          ).pipe(
            concatMap((result) =>
              of(...result).pipe(
                concatMap(([cropId, selectedCropFieldAdvices]) =>
                  this.copyTreatmentsWarnCopyingToGWBDialog(
                    cropId,
                    sourceCropFieldAdvice,
                    selectedCropFieldAdvices,
                    treatmentType,
                  ),
                ),
                concatMap(({ cropId, selectedCropFieldAdvices }) =>
                  cropId === sourceCropFieldAdvice.crop.id
                    ? of(...selectedCropFieldAdvices)
                    : this.copyTreatmentsToNonMatchingCropDialog(
                        sourceCropFieldAdvice.treatments,
                        sourceCropFieldAdvice.crop.name,
                        selectedCropFieldAdvices,
                        treatmentType,
                      ),
                ),
                scan((acc, curr) => [...acc, curr], []),
                startWith([]),
                last(),
              ),
            ),
            map(
              (selectedCropFieldAdvices) =>
                new CopyTreatments(
                  sourceCropFieldAdvice,
                  selectedCropFieldAdvices,
                  treatmentType,
                  copyExecutedTreatments,
                ),
            ),
          ),
      ),
    ),
  );

  copyTreatments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.CopyTreatments),
      withLatestFrom(this.store.pipe(select(selectExecutedTreatments))),
      concatMap(
        ([
          { sourceCropFieldAdvice, targetCropFieldAdvices, treatmentType, copyExecutedTreatments },
          executedTreatments,
        ]: [CopyTreatments, ExecutedTreatmentWithNitrogenEffectivity[]]) =>
          this.copyTreatmentsNotifyDialog(
            sourceCropFieldAdvice.treatments,
            targetCropFieldAdvices,
            treatmentType,
            sourceCropFieldAdvice.cropYear,
            true,
            copyExecutedTreatments,
            executedTreatments,
          ).pipe(
            concatMap(({ status, result }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? of({
                    sourceCropFieldAdvice,
                    targetCropFieldAdvices,
                    treatmentType,
                    copyExecutedTreatments,
                    removeExistingTreatments: result.removeExistingTreatments,
                  })
                : EMPTY,
            ),
          ),
      ),
      switchMap(
        ({
          sourceCropFieldAdvice,
          targetCropFieldAdvices,
          treatmentType,
          copyExecutedTreatments,
          removeExistingTreatments,
        }) =>
          !!sourceCropFieldAdvice && targetCropFieldAdvices && targetCropFieldAdvices.length > 0
            ? this.api
                .copyTreatments(
                  sourceCropFieldAdvice.adviceId,
                  targetCropFieldAdvices.map((advice) => advice.adviceId),
                  treatmentType,
                  copyExecutedTreatments,
                  removeExistingTreatments,
                )
                .pipe(
                  switchMap(() =>
                    concat(
                      of(
                        CoreActions.setLoading({ loading: false }),
                        new CopyTreatmentsSuccess(),
                        new UpdatedTreatments(),
                      ),
                    ),
                  ),
                  catchError(() => of(CopyAdviceActions.completedCopyTreatmentsError())),
                  startWith(CoreActions.setLoading({ loading: true })),
                  finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                )
            : EMPTY,
      ),
    ),
  );

  saveTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.SaveTreatment),
      switchMap(({ adviceId, treatment }: SaveTreatment) => {
        const saveTreatment = {
          ...treatment,
          aidAdvices: treatment.aidAdvices.map(({ cropAidId, dosage, nitrogenEffectivity }) => ({
            cropAidId,
            dosage,
            nitrogenEffectivity,
          })),
        };
        delete saveTreatment.isAnExecutedTreatment;
        return this.api.upsertTreatment(adviceId, saveTreatment).pipe(
          concatMap(() => [
            new SaveTreatmentSuccess(treatment.treatmentType),
            new UpdatedTreatments(),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  copyTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.CopyTreatment),
      withLatestFrom(this.store.pipe(select(selectEditingAdvice))),
      map(
        ([{ treatment }, cropFieldAdvice]: [CopyTreatment, CropFieldAdvice]) =>
          new SaveTreatment(cropFieldAdvice.adviceId, treatment),
      ),
    ),
  );

  saveTreatments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.SaveTreatments),
      switchMap(({ adviceId, treatments }: SaveTreatments) =>
        this.api.upsertTreatments(adviceId, treatments).pipe(
          concatMap(() => [new SaveTreatmentsSuccess(), new UpdatedTreatments()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  copyTreatmentToOtherAdvicesDialogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.CopyTreatmentToOtherAdvicesDialogs),
      withLatestFrom(
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectAvailableCropFieldAdvices())),
        this.store.pipe(select(selectCrops)),
      ),
      concatMap(
        ([{ treatment, treatmentType }, sourceCropFieldAdvice, targetCropFieldAdvices, crops]: [
          CopyTreatmentToOtherAdvicesDialogs,
          CropFieldAdvice,
          CropFieldAdvice[],
          CropData[],
        ]) =>
          this.copyTreatmentsSelectCropFieldsDialog(
            sourceCropFieldAdvice,
            targetCropFieldAdvices,
            treatmentType,
            crops,
          ).pipe(
            concatMap((result) =>
              of(...result).pipe(
                concatMap(([cropId, selectedCropFieldAdvices]) =>
                  cropId === sourceCropFieldAdvice.crop.id
                    ? of(...selectedCropFieldAdvices)
                    : this.copyTreatmentsToNonMatchingCropDialog(
                        [treatment],
                        sourceCropFieldAdvice.crop.name,
                        selectedCropFieldAdvices,
                        treatmentType,
                      ),
                ),
                scan((acc, curr) => [...acc, curr], []),
                startWith([]),
                last(),
              ),
            ),
            filter(
              (selectedCropFieldAdvices) =>
                !!treatment && !!selectedCropFieldAdvices?.length && !!treatmentType,
            ),
            switchMap((selectedCropFieldAdvices) =>
              this.copyTreatmentsWarnCopyingToGWBDialog(
                sourceCropFieldAdvice.crop.id,
                sourceCropFieldAdvice,
                selectedCropFieldAdvices,
                treatmentType,
              ),
            ),
            switchMap(({ selectedCropFieldAdvices }) =>
              this.copyTreatmentsNotifyDialog(
                [treatment],
                selectedCropFieldAdvices,
                treatmentType,
                selectedCropFieldAdvices[0].cropYear,
                false,
                true,
              ).pipe(
                concatMap((notifyResult) =>
                  notifyResult.status === DIALOG_RETURN_TYPES.confirm
                    ? of({
                        treatment,
                        selectedCropFieldAdvices,
                      })
                    : EMPTY,
                ),
              ),
            ),
            map(
              (resultData) =>
                new CopyTreatmentsToOtherAdvices(
                  sourceCropFieldAdvice,
                  [resultData.treatment],
                  resultData.selectedCropFieldAdvices,
                ),
            ),
          ),
      ),
    ),
  );

  copyExecutedTreatmentToOtherAdvicesDialogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.CopyExecutedTreatmentToOtherAdvicesDialogs),
      withLatestFrom(
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectAvailableCropFieldAdvices())),
        this.store.pipe(select(selectCrops)),
      ),
      concatMap(
        ([{ treatment, treatmentType }, sourceCropFieldAdvice, targetCropFieldAdvices, crops]: [
          CopyExecutedTreatmentToOtherAdvicesDialogs,
          CropFieldAdvice,
          CropFieldAdvice[],
          CropData[],
        ]) =>
          this.copyTreatmentsSelectCropFieldsDialog(
            sourceCropFieldAdvice,
            targetCropFieldAdvices,
            treatmentType,
            crops,
          ).pipe(
            concatMap((result) =>
              of(...result).pipe(
                concatMap(([cropId, selectedCropFieldAdvices]) =>
                  cropId === sourceCropFieldAdvice.crop.id
                    ? of(...selectedCropFieldAdvices)
                    : this.copyTreatmentsToNonMatchingCropDialog(
                        [treatment],
                        sourceCropFieldAdvice.crop.name,
                        selectedCropFieldAdvices,
                        treatmentType,
                      ),
                ),
                scan((acc, curr) => [...acc, curr], []),
                startWith([]),
                last(),
              ),
            ),
            filter(
              (selectedCropFieldAdvices) =>
                !!treatment && !!selectedCropFieldAdvices?.length && !!treatmentType,
            ),
            switchMap((selectedCropFieldAdvices) =>
              this.copyTreatmentsNotifyDialog(
                [treatment],
                selectedCropFieldAdvices,
                treatmentType,
                selectedCropFieldAdvices[0].cropYear,
                false,
              ).pipe(
                concatMap((notifyResult) =>
                  notifyResult.status === DIALOG_RETURN_TYPES.confirm
                    ? of({
                        treatment,
                        selectedCropFieldAdvices,
                      })
                    : EMPTY,
                ),
              ),
            ),
            map(
              (resultData) =>
                new CopyExecutedTreatmentsToOtherAdvices(
                  sourceCropFieldAdvice,
                  [resultData.treatment],
                  resultData.selectedCropFieldAdvices,
                ),
            ),
          ),
      ),
    ),
  );

  copyTreatmentsToOtherAdvices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.CopyTreatmentsToOtherAdvices),
      mergeMap(
        ({
          sourceCropFieldAdvice,
          treatments,
          targetCropFieldAdvices,
        }: CopyTreatmentsToOtherAdvices) =>
          this.api
            .copyByTreatments(
              sourceCropFieldAdvice.adviceId,
              targetCropFieldAdvices.map((t) => t.adviceId),
              treatments.map((t) => t.id),
            )
            .pipe(
              switchMap(() => concat(of(new CopyTreatmentsSuccess(), new UpdatedTreatments()))),
              catchError(() => of(CopyAdviceActions.completedCopyTreatmentsError())),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            ),
      ),
    ),
  );

  copyExecutedTreatmentsToOtherAdvices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.CopyExecutedTreatmentsToOtherAdvices),
      mergeMap(
        ({
          sourceCropFieldAdvice,
          executedTreatments,
          targetCropFieldAdvices,
        }: CopyExecutedTreatmentsToOtherAdvices) =>
          this.api
            .copyByExecutedTreatments(
              sourceCropFieldAdvice.adviceId,
              targetCropFieldAdvices.map((t) => t.adviceId),
              executedTreatments.map((t) => t.id),
            )
            .pipe(
              switchMap(() => concat(of(new CopyTreatmentsSuccess(), new UpdatedTreatments()))),
              catchError(() => of(CopyAdviceActions.completedCopyTreatmentsError())),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            ),
      ),
    ),
  );

  removeTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.RemoveTreatment),
      withLatestFrom(this.store.pipe(select(selectEditingAdvice))),
      switchMap(([{ treatment }, advice]: [RemoveTreatment, CropFieldAdvice]) =>
        this.dialog
          .confirm({
            title: this.locale.translate('clearExecutedTreatmentTitle'),
            text: this.locale.translate('clearExecutedTreatmentText'),
          })
          .pipe(
            switchMap(({ status }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? of(new RemoveTreatmentRequest(advice.adviceId, treatment.id))
                : EMPTY,
            ),
          ),
      ),
    ),
  );

  removeTreatmentRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.RemoveTreatmentRequest),
      switchMap(({ adviceId, treatmentId }: RemoveTreatmentRequest) =>
        this.api.removeTreatment(adviceId, treatmentId).pipe(
          concatMap(() => [new RemoveTreatmentSuccess(), new UpdatedTreatments()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  updatedTreatments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.UpdatedTreatments),
      delay(100),
      map((_) => new GetCropFieldAdvices()),
    ),
  );

  clearAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.ClearAdviceConfirmed),
      withLatestFrom(this.store.pipe(select(selectEditingAdvice))),
      switchMap(([action, advice]: [ClearAdviceConfirmed, CropFieldAdvice]) =>
        this.api.clearAdvice(advice.adviceId, action.treatmentType).pipe(
          concatMap(() => [
            new ClearAdviceConfirmedSuccess(),
            new UpdatedTreatments(),
            new OpenSelectSchemaDialog(),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  clearAdvicePending$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.ClearAdvicePending),
      withLatestFrom(this.store.pipe(select(selectEditingTreatments))),
      switchMap(([{ treatmentType }, treatments]) =>
        treatments.length
          ? this.dialog
              .confirm({
                title: this.locale.translate('clearAdviceTitle'),
                text: this.locale.translate('clearAdviceText'),
              })
              .pipe(
                switchMap(({ status }) =>
                  status === DIALOG_RETURN_TYPES.confirm
                    ? of(new ClearAdviceConfirmed(treatmentType))
                    : EMPTY,
                ),
              )
          : this.dialog
              .notify({
                title: '',
                text: this.locale.translate('clearAdviceEmptyText'),
              })
              .pipe(switchMap(() => EMPTY)),
      ),
    ),
  );

  getFertilizationSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetFertilizationSetting>(AdviceActionTypes.GetFertilizationSetting),
      withLatestFrom(this.store.pipe(select(selectFertilizationSettings))),
      concatMap(([{ adviceId }, fertilizationSettings]) => {
        const setting = fertilizationSettings.find((x) => x.adviceId === adviceId);

        if (!setting?.isAcceptedByUser) {
          return [new SetFertilizationSetting(setting), new OpenFertilizationSettingDialog()];
        }

        return [new SetFertilizationSetting(setting), new SelectSchemaSilently()];
      }),
    ),
  );

  saveFertilizationSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveFertilizationSetting>(AdviceActionTypes.SaveFertilizationSetting),
      withLatestFrom(
        this.store.pipe(select(selectFertilizationSetting)),
        this.store.pipe(select(getCropYear)),
      ),
      mergeMap(([{ fertilizationSetting }, existingFertilizationSetting, cropYear]) => {
        const updatedFertilizationSetting = {
          ...existingFertilizationSetting,
          ...fertilizationSetting,
        } as FertilizationSettingWithIds;

        return this.api
          .saveFertilizationSetting({ cropYear, ...updatedFertilizationSetting })
          .pipe(
            map(() => new SaveFertilizationSettingSuccess(updatedFertilizationSetting)),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
      }),
    ),
  );

  openFertilizationSettingDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType<OpenFertilizationSettingDialog>(AdviceActionTypes.OpenFertilizationSettingDialog),
      withLatestFrom(
        this.store.pipe(select(selectFertilizationSetting)),
        this.store.pipe(select(selectCrops)),
        this.store.pipe(select(selectNonProductiveCrops)),
        this.store.pipe(select(selectGreenManureCrops)),
        this.store.pipe(select(selectEditingAdvice)),
      ),
      switchMap(
        ([_, fertilizationSetting, crops, nonProductiveCrops, greenManureCrops, cropFieldAdvice]) =>
          this.dialog
            .form(FertilizationSettingDialogComponent, {
              title: this.locale.translate('fertilizationSetting'),
              cropId:
                crops.find((crop) => crop.id === cropFieldAdvice.crop.id)?.agrifirmCrop.id ?? 0,
              crops,
              nonProductiveCrops,
              greenManureCrops,
              fertilizationSetting:
                fertilizationSetting == null
                  ? {
                      id: 0,
                      adviceId: 0,
                      customerId: 0,
                      isAcceptedByUser: false,
                      isPrecedingCrop: false,
                      isGreenManure: false,
                      shouldBeUsedInUsageStandard: true,
                      shouldBeUsedInLegalStandard: true,
                      shouldUseTransitionalArrangement: false,
                      shouldUseLowInPhosphateArrangement: false,
                      greenManureSettings: null,
                      precedingCropSettings: null,
                      nitrogenCalculationSettings: null,
                      organicMatterSupply: OrganicMatterSupply.Average,
                      expectedCropYield: 0,
                      fertilizationBalanceSettings: null,
                    }
                  : fertilizationSetting,
            })
            .pipe(
              switchMap(({ status, result }) =>
                status === DIALOG_RETURN_TYPES.confirm
                  ? of(
                      new SaveFertilizationSetting({
                        ...result,
                      }),
                    )
                  : of(new SelectSchemaSilently()),
              ),
            ),
      ),
    ),
  );

  openCompanySettingsDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.OpenCompanySettingsDialog),
      withLatestFrom(
        this.store.pipe(select(selectCompanyGrasslandUsage)),
        this.store.pipe(select(selectAvailableCropFieldAdvices())),
        this.store.pipe(select(selectCompanySettings)),
        this.store.pipe(select(getCropYear)),
      ),
      switchMap(([_, companyGrasslandUsage, cropFieldAdvices, companySettings, cropYear]) =>
        this.dialog
          .form(CompanySettingsDialogComponent, {
            title: this.locale.translate('companySettings'),
            grasslandUsage: this.hideGrasslandUsageInput(cropFieldAdvices)
              ? null
              : companyGrasslandUsage,
            driftReduction: companySettings.driftReduction,
            yearlySettings: companySettings.yearlySettings ?? {
              year: cropYear,
              nitrogenCuts: {
                cutsInHaPeriod1: 0,
                cutsInHaPeriod2: 0,
                cutsInHaPeriod3: 0,
              },
              showReminderForGrainRegistration: false,
            },
            defaultSoilTypeId: companySettings.defaultSoilTypeId,
            automaticAdvice: companySettings.automaticAdvice,
          })
          .pipe(
            switchMap(({ status, result }) => {
              if (status === DIALOG_RETURN_TYPES.confirm) {
                const actions = [];

                if (!this.hideGrasslandUsageInput(cropFieldAdvices)) {
                  actions.push(new UpdateGrasslandUsages(result.companySettings.grasslandUsage));
                }

                actions.push(new UpdateCompanySettings(result.companySettings));

                return of(...actions);
              } else {
                return EMPTY;
              }
            }),
          ),
      ),
    ),
  );

  updateCompanySettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateCompanySettings>(AdviceActionTypes.UpdateCompanySettings),
      switchMap((parentEffect) =>
        this.arableCustomerService
          .upsertCompanySettings({
            driftReduction: parentEffect.companySettings.driftReduction,
            yearlySettings: parentEffect.companySettings.yearlySettings,
            defaultSoilTypeId: parentEffect.companySettings.defaultSoilTypeId,
            automaticAdvice: parentEffect.companySettings.automaticAdvice,
          })
          .pipe(
            map(() => {
              this.store.dispatch(triggerDelayedGetFertilizationLegalStandards());
              this.store.dispatch(new GetCropFieldAdvices());
              return new GetCompanySettings();
            }),
          ),
      ),
    ),
  );

  openEditAllFertilizationSettingsDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType<OpenEditAllFertilizationSettingsDialog>(
        AdviceActionTypes.OpenEditAllFertilizationSettingsDialog,
      ),
      withLatestFrom(
        this.store.pipe(select(selectFertilizationSettings)),
        this.store.pipe(select(selectCrops)),
        this.store.pipe(select(selectNonProductiveCrops)),
        this.store.pipe(select(selectGreenManureCrops)),
        this.store.pipe(select(selectCropFieldAdvices)),
      ),
      switchMap(
        ([
          _,
          fertilizationSetting,
          crops,
          nonProductiveCrops,
          greenManureCrops,
          cropFieldAdvices,
        ]) =>
          this.dialog
            .form(
              EditAllFertilizationSettingsDialogComponent,
              {
                title: this.locale.translate('editAllFertilizationSettings'),
                cropFieldAdvices,
                crops,
                nonProductiveCrops,
                greenManureCrops,
                fertilizationSettingsWithName: fertilizationSetting,
              },
              { maxWidth: '90vw', width: '100%' },
            )
            .pipe(
              switchMap(({ status, result }) =>
                status === DIALOG_RETURN_TYPES.confirm
                  ? result.fertilizationSettingsWithName?.map(
                      (fertilizationSettingWithName) =>
                        new SaveFertilizationSetting(fertilizationSettingWithName),
                    )
                  : EMPTY,
              ),
            ),
      ),
    ),
  );

  loadNitrogenCalculationSettingToggles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadNitrogenCalculationSettingToggles>(
        AdviceActionTypes.LoadNitrogenCalculationSettingToggles,
      ),
      switchMap(({ adviceId }) =>
        this.api.getNitrogenCalculationSettingToggles(adviceId).pipe(
          map(
            (nitrogenCalculationSettingToggles) =>
              new SetNitrogenCalculationSettingToggles(nitrogenCalculationSettingToggles),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadNitrogenCalculationSettingTogglesByAdvices$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadNitrogenCalculationSettingTogglesByAdvices>(
        AdviceActionTypes.LoadNitrogenCalculationSettingTogglesByAdvices,
      ),
      switchMap(({ adviceIds }) =>
        this.api.getNitrogenCalculationSettingTogglesByAdvices(adviceIds).pipe(
          map((result) => new SetNitrogenCalculationSettingTogglesByAdvices(result.settingToggles)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  toggleAdviceCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ToggleAdviceCompleted>(AdviceActionTypes.ToggleAdviceCompleted),
      withLatestFrom(this.store.pipe(select(selectEditingAdvice))),
      switchMap(([{ treatmentType }, editingAdvice]) => {
        if (
          (treatmentType === TreatmentType.Fertilization && editingAdvice.fertilizationCompleted) ||
          (treatmentType === TreatmentType.GBM && editingAdvice.gbmCompleted)
        ) {
          return this.api.reopenAdvice(editingAdvice.adviceId, treatmentType).pipe(
            map((_) => new GetCropFieldAdvices()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        } else {
          return this.api.completeAdvice(editingAdvice.adviceId, treatmentType).pipe(
            map((_) => new GetCropFieldAdvices()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
      }),
    ),
  );

  getFertilizationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AdviceActionTypes.GetFertilizationSettings,
        AdviceActionTypes.SaveFertilizationSettingSuccess,
      ),
      withLatestFrom(
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(selectEditingAdvice)),
      ),
      switchMap(([_, cropYear, advice]) =>
        this.api.getFertilizationSettings(cropYear).pipe(
          concatMap((result) => {
            if (advice !== null) {
              return [
                new SetFertilizationSettings(result.fertilizationSettings),
                new SetFertilizationSetting(
                  result.fertilizationSettings.find((x) => x.adviceId === advice.adviceId),
                ),
              ];
            }
            return [new SetFertilizationSettings(result.fertilizationSettings)];
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  updateExpectedCropYield$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.UpdateExpectedCropYield),
      switchMap(({ adviceId, cropYear, expectedCropYield }) =>
        this.api
          .patchExpectedCropYieldProduct(
            adviceId,
            cropYear,
            expectedCropYield,
          )
          .pipe(
            map(() => new UpdateExpectedCropYieldSuccess(
              adviceId,
              expectedCropYield,
            )),
            catchError(() => of(new UpdateExpectedCropYieldFailure())),
          ),
      ),
    ),
  );

  updateExpectedCropYieldSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.UpdateExpectedCropYieldSuccess),
      concatMap(() => [
        new SaveFertilizationSettingSuccess(),
        NotificationActions.successSnackbar({
          message: this.locale.translate('fertilizationBalance.updateExpectedCropYieldSuccess'),
        }),
      ]),
    ),
  );

  updateExpectedCropYieldFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.UpdateExpectedCropYieldFailure),
      map(() =>
        NotificationActions.errorSnackbar({
          message: this.locale.translate('fertilizationBalance.validation.GenericErrorUpdatingExpectedCropYield'),
        }),
      ),
    ),
  );

  getCustomerFertilizerStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.GetCustomerFertilizerStock),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) =>
        this.api.getCustomerFertilizerStock(cropYear).pipe(
          mergeMap((result) => of(new SetCustomerFertilizerStock(result))),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getRecommendedNutrientQuantityByAdviceIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.GetRecommendedNutrientQuantityByAdviceIds),
      switchMap(({ cropFieldIds, cropYear }: { cropFieldIds: number[]; cropYear: number }) =>
        this.api.getCropFieldNutrientRequisitesByAdviceIds(cropFieldIds, cropYear).pipe(
          map((result) => new SetRecommendedNutrientQuantityByAdviceIds(result)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getLatestSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.GetLatestSoilAnalysis),
      switchMap(({ soilAnalysisIds }: GetLatestSoilAnalysis) =>
        this.api.getLatestSoilAnalysis(soilAnalysisIds).pipe(
          map((soilAnalysis: SoilAnalysis) => new SetLatestSoilAnalysis(soilAnalysis)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  selectCustomerForImport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdviceActionTypes.SelectCustomerForImport),
        concatMap(() =>
          this.dialog
            .dialog(
              SelectCustomerDialogComponent,
              { title: this.locale.translate('selectCustomer') },
              { height: '100%', width: '100%', maxHeight: '65vh' },
            )
            .pipe(() => EMPTY),
        ),
      ),
    { dispatch: false },
  );

  getAdvicesForCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAdvicesForCustomer>(AdviceActionTypes.GetAdvicesForCustomer),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      concatMap(([{ customerId }, cropYear]) =>
        this.api.getAdvicesByCustomerAndCropYear(customerId, cropYear).pipe(
          catchError((error: HttpErrorResponse) =>
            error.status === 403
              ? this.dialog
                  .notify({
                    title: this.locale.translate('noAccess'),
                    text: this.locale.translate('noAccessCustomer'),
                  })
                  .pipe(() => EMPTY)
              : throwError(error),
          ),
          map((advices) => new SelectAdviceForImport(advices, customerId)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  selectAdviceForImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SelectAdviceForImport>(AdviceActionTypes.SelectAdviceForImport),
      withLatestFrom(
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectEditingTreatmentType)),
      ),
      concatMap(([{ advices, customerId }, editingAdvice, treatmentType]) =>
        this.dialog
          .select({
            options: advices
              .filter((advice) => advice.crop.id === editingAdvice.crop.id)
              .map((advice) => ({
                name: `${advice.name}`,
                description: `${this.locale.translate('treatments')}: ${
                  (advice.treatments || []).filter(
                    (treatment) => treatment.treatmentType === treatmentType,
                  ).length
                }`,
                value: advice,
              }))
              .sort((a, b) => a.name.localeCompare(b.name)),
            title: this.locale.translate('selectAdvice'),
            selected: null,
            emptyText: this.locale.translate('noAdvices'),
          })
          .pipe(
            filter(({ status }) => status === DIALOG_RETURN_TYPES.confirm),
            map(({ result }) => new ImportAdviceFromCustomer(customerId, result.adviceId)),
          ),
      ),
    ),
  );

  importAdviceFromCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ImportAdviceFromCustomer>(AdviceActionTypes.ImportAdviceFromCustomer),
      withLatestFrom(
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectEditingTreatmentType)),
      ),
      concatMap(([{ adviceId, customerId }, editingAdvice, treatmentType]) =>
        this.api
          .importAdviceFromCustomer(editingAdvice.adviceId, adviceId, customerId, treatmentType)
          .pipe(
            map((_) => new UpdatedTreatments()),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            startWith(CoreActions.setLoading({ loading: true })),
          ),
      ),
    ),
  );

  triggerEditFertilizationBalanceSettingsDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType<TriggerEditFertilizationBalanceSettingsDialog>(
        AdviceActionTypes.TriggerEditFertilizationBalanceSettingsDialog,
      ),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) =>
        combineLatest([
          this.api.getFertilizationBalanceSettings(cropYear),
          this.api.getFertilizationSettings(cropYear),
        ]).pipe(
          map(([fertilizationBalanceSettingsResult, fertilizationSettingsResult]) =>
            this.createOpenEditFertilizationBalanceSettingsDialogAction(
              fertilizationBalanceSettingsResult,
              fertilizationSettingsResult,
            ),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  openEditFertilizationBalanceSettingsDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType<OpenEditFertilizationBalanceSettingsDialog>(
        AdviceActionTypes.OpenEditFertilizationBalanceSettingsDialog,
      ),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([{ fertilizationBalanceSettings, headerBusinessRuleViolations }, cropYear]) =>
        this.dialog
          .fullscreenDialog(
            EditFertilizationBalanceSettingsDialogComponent,
            {
              title: this.locale.translate('fertilizationBalance.sectionTitle'),
              titleIcon: this.containsWarnings(headerBusinessRuleViolations),
              titleIconType: 'warning',
              titleIconColor: 'warn',
              subTitle: cropYear.toString(),
              confirmButtonText: this.locale.translate('fertilizationBalance.done'),
              setPendingChanges: false,
              fertilizationBalanceSettings,
              headerBusinessRuleViolations,
              cropYear,
            },
            { maxWidth: '90vw', width: '100%' },
          )
          .pipe(() => of(new DoneEditingFertilizationBalanceSettings())),
      ),
    ),
  );

  private copyTreatmentsSelectCropFieldsDialog(
    sourceCropFieldAdvice: CropFieldAdvice,
    targetCropFieldAdvices: CropFieldAdvice[],
    treatmentType: TreatmentType,
    crops: CropData[],
  ) {
    const options = targetCropFieldAdvices
      .filter(
        (cropFieldAdvice) => cropFieldAdvice.cropFieldId !== sourceCropFieldAdvice.cropFieldId,
      )
      .map((cropFieldAdvice) => {
        const hectares = `${this.formatDecimal(cropFieldAdvice.areaInHectares)} Ha`;
        const cropName = cropFieldAdvice.crop.name;
        const cropVariety = cropFieldAdvice?.cropVariety?.name
          ? ' (' + cropFieldAdvice.cropVariety.name + '),'
          : ',';
        const totalTreatments = cropFieldAdvice.treatments.filter(
          (t) => t.treatmentType === treatmentType,
        ).length;
        const treatmentsText = this.locale
          .translate(totalTreatments === 1 ? 'treatment' : 'treatments')
          .toLowerCase();
        return {
          name: `${cropFieldAdvice.name} (${hectares})${
            cropFieldAdvice.isGroundWaterPreservation ? ' - GWB' : ''
          }`,
          description: `${cropName}${cropVariety} ${totalTreatments} ${treatmentsText}`,
          value: cropFieldAdvice,
        };
      });

    const selectCropFieldsDialogData: SelectCropFieldsDialogData = {
      options,
      crops,
      selectedCropIds: [sourceCropFieldAdvice.crop.id],
      title: this.locale.translate('selectCropFieldsTitle'),
      confirmButton: options && options.length > 0,
      selected: null,
      confirmButtonText: this.locale.translate('select'),
      cancelButtonText: this.locale.translate('cancel'),
      emptyText: '',
      pushHistoryState: false,
      fullscreen: false,
      disableSelectAll: false,
    };

    return this.dialog.form(SelectCropFieldsDialogComponent, selectCropFieldsDialogData).pipe(
      switchMap(({ status, result }) =>
        status !== DIALOG_RETURN_TYPES.confirm ? EMPTY : of(result),
      ),
      map((selectedCropFieldAdvices) =>
        selectedCropFieldAdvices.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.crop.id.toString()]: [...(acc[curr.crop.id.toString()] || []), curr],
          }),
          {} as { [cropId: string]: CropFieldAdvice[] },
        ),
      ),
      map(
        (selectedCropFieldAdvicesMapByCropId) =>
          Object.keys(selectedCropFieldAdvicesMapByCropId).map((key) => [
            parseInt(key, 10),
            selectedCropFieldAdvicesMapByCropId[key],
          ]) as [number, CropFieldAdvice[]][],
      ),
    );
  }

  private copyTreatmentsWarnCopyingToGWBDialog(
    cropId: number,
    sourceCropFieldAdvice: CropFieldAdvice,
    selectedCropFieldAdvices: CropFieldAdvice[],
    treatmentType: TreatmentType,
  ) {
    // Skip this warning dialog if none of the selected advices are GWB, or both the source & target(s) are GWB
    if (
      treatmentType === TreatmentType.Fertilization ||
      !selectedCropFieldAdvices.some(
        (cropFieldAdvice) => cropFieldAdvice.isGroundWaterPreservation,
      ) ||
      (sourceCropFieldAdvice.isGroundWaterPreservation &&
        selectedCropFieldAdvices.every(
          (cropFieldAdvice) => cropFieldAdvice.isGroundWaterPreservation,
        ))
    ) {
      return of({
        cropId,
        selectedCropFieldAdvices,
      });
    }

    return this.dialog
      .confirm(
        {
          title: this.locale.translate('copyTreatmentsToGWBWarningTitle'),
          text: this.locale.translate('copyTreatmentsToGWBWarningDescription'),
          confirmButtonText: this.locale.translate('cancel'),
          confirmButtonColor: 'warn',
          cancelButtonText: this.locale.translate('continueAnyway'),
          cancelButtonColor: 'warn',
        },
        { maxWidth: '500px' },
      )
      .pipe(
        switchMap(({ status }) =>
          status === DIALOG_RETURN_TYPES.confirm
            ? this.generateCopyTreatmentsSelectCropFieldsCancelResult(
                cropId,
                selectedCropFieldAdvices,
              )
            : of({
                cropId,
                selectedCropFieldAdvices,
              }),
        ),
      );
  }

  private generateCopyTreatmentsSelectCropFieldsCancelResult(
    cropId: number,
    selectedCropFieldAdvices: CropFieldAdvice[],
  ) {
    const scfaWithoutGWP = selectedCropFieldAdvices.filter((cfa) => !cfa.isGroundWaterPreservation);
    return scfaWithoutGWP.length === 0
      ? EMPTY
      : of({
          cropId,
          selectedCropFieldAdvices: scfaWithoutGWP,
        });
  }

  private copyTreatmentsToNonMatchingCropDialog(
    sourceTreatments: TreatmentOrExecutedTreatment[],
    sourceCropName: string,
    targetCropFieldAdvices: CropFieldAdvice[],
    treatmentType: TreatmentType,
  ) {
    const treatments = sourceTreatments.filter(
      (treatment) => treatment.treatmentType === treatmentType,
    );
    return this.dialog
      .confirm({
        title: this.locale.translate('copyTreatmentsToNonmatchingCrop'),
        text: `${this.locale.translate('copy')} ${treatments.length} ${this.locale
          .translate(treatments.length === 1 ? 'treatment' : 'treatments')
          .toLowerCase()} ${this.locale.translate(
          'copyFor',
        )} ${sourceCropName} ${this.locale.translate('copyTo')} ${
          targetCropFieldAdvices.length
        } ${this.locale.translate(
          targetCropFieldAdvices.length === 1 ? 'cropField' : 'cropFields',
        )} ${targetCropFieldAdvices[0].crop.name}`,
        confirmButtonText: this.locale.translate('copy'),
        cancelButtonText: this.locale.translate('skip'),
      })
      .pipe(
        switchMap(({ status }) =>
          status !== DIALOG_RETURN_TYPES.confirm ? EMPTY : of(...targetCropFieldAdvices),
        ),
      );
  }

  private copyTreatmentsNotifyDialog(
    sourceTreatments: TreatmentOrExecutedTreatment[],
    targetAdvices: CropFieldAdvice[],
    treatmentType: TreatmentType,
    cropYear: number,
    askForRemoveExistingTreatmentsConfirmation: boolean,
    copyExecutedTreatments = false,
    executedTreatments: ExecutedTreatmentWithNitrogenEffectivity[] = [],
  ): Observable<{ status: DIALOG_RETURN_TYPES; result: CopyAdviceConfirmationDialogOutputModel }> {
    if (targetAdvices.length === 0) {
      return EMPTY;
    }
    const treatments = sourceTreatments.filter(
      (treatment) => treatment.treatmentType === treatmentType,
    );
    let treatmentCount = treatments.length;
    if (copyExecutedTreatments) {
      treatmentCount += executedTreatments?.length;
    }
    const title = this.locale.translate('copyTitle');
    let text = '';

    // Warning forbidden cropAids:
    if (this.wouldCopyForbiddenCropAidsToPlanetProofAdvice(treatments, targetAdvices)) {
      text = this.locale.translate('wouldCopyForbiddenCropAidsToPlanetProofAdvice');
      return this.dialog.notify({ title, text }).pipe(map(() => ({ status: null, result: null })));
    }

    // "There are X treatments being copied to Y cropFields."
    text = `
    ${this.locale.translate(treatmentCount > 1 ? 'thereAreDutch' : 'thereIsDutch')}
    ${treatmentCount}
    ${this.locale.translate(treatmentCount > 1 ? 'treatments' : 'treatment').toLowerCase()}
    ${this.locale.translate(treatmentCount > 1 ? 'copiedToMultiple' : 'copiedToOne')}
    ${targetAdvices.length}
    ${this.locale.translate(targetAdvices.length > 1 ? 'cropFields' : 'cropField')}.`;

    // Warning proprietary fertilizers
    if (targetAdvices.map((t) => t.cropYear).some((y) => y !== cropYear)) {
      text += `
      <br /><br />
      <b>${this.locale.translate('warning')}</b>:
      ${this.locale.translate('warningProprietaryFertilizers')}`;
    }

    // Show the custom confirmation dialog, which also asks the use if they want to remove
    // all existing treatments, or add all treatments next to the existing ones in target advices
    return this.dialog
      .dialog(CopyAdviceConfirmationDialogComponent, {
        title,
        text,
        askForRemoveExistingTreatmentsConfirmation,
        confirmButton: true,
        confirmButtonText: this.locale.translate('copy'),
        cancelButtonText: this.locale.translate('cancel'),
      })
      .pipe(switchMap(({ status, result }) => of({ status, result })));
  }

  private wouldCopyForbiddenCropAidsToPlanetProofAdvice = (
    treatments: TreatmentOrExecutedTreatment[],
    targetAdvices: CropFieldAdvice[],
  ): boolean =>
    targetAdvices.some((a) => a.isPlanetProof) &&
    treatments.reduce((x, t) => [...x, ...t.aidAdvices], []).some((a) => a.forbiddenForPlanetProof);

  private formatDecimal(num: number) {
    return formatNumber(num, this.locale.getLocale());
  }

  private buildInvalidTreatmentsMessage(invalidTreatment: Treatment, cropAids: CropAid[]) {
    const invalidAidAdvices = invalidTreatment.aidAdvices.filter(
      (aidAdvice) => !cropAids.find((cropAid) => cropAid.id === aidAdvice.cropAidId).isActive,
    );

    const message = `${this.locale.translate('treatment')} '${
      invalidTreatment.name
    }' ${this.locale.translate('containsInvalidCropAids')}: '${invalidAidAdvices
      .map((aidAdvice) => cropAids.find((cropAid) => cropAid.id === aidAdvice.cropAidId).name)
      .join(', ')}'`;

    return message;
  }

  private getAddTreatmentName(treatment: Treatment, editingTreatments: Treatment[]) {
    for (const editingTreatment of editingTreatments) {
      if (this.areTreatmentsEqual(treatment, editingTreatment)) {
        return `${treatment.name} (${this.locale.translate('adopted')})`;
      }
    }
    return treatment.name;
  }

  private areTreatmentsEqual(t: Treatment, e: Treatment) {
    return (
      t.aidAdvices.length === e.aidAdvices.length &&
      t.frequency === e.frequency &&
      t.moment === e.moment &&
      t.name === e.name &&
      t.objective === e.objective &&
      t.periodId === e.periodId &&
      t.pointOfAttention === e.pointOfAttention &&
      t.treatmentType === e.treatmentType
    );
  }

  private hideGrasslandUsageInput = (cropFieldAdvices: CropFieldAdvice[]) =>
    !cropFieldAdvices ||
    cropFieldAdvices.length === 0 ||
    cropFieldAdvices.filter((advice) => advice.crop.id === this.env.GRASSLAND_CROP_ID).length === 0;

  private containsWarnings(warnings?: string[]): boolean {
    return warnings?.length > 0;
  }

  private createOpenEditFertilizationBalanceSettingsDialogAction(
    fertilizationBalanceSettingsResult: FertilizationBalanceSettingsResponse,
    fertilizationSettingsResult: { fertilizationSettings: FertilizationSettingWithName[] },
  ): OpenEditFertilizationBalanceSettingsDialog {
    const fertilizationSettingsByAdviceId = new Map(
      fertilizationSettingsResult.fertilizationSettings.map((setting) => [setting.adviceId, setting] as const),
    );
    const mergedFertilizationBalanceSettings =
      fertilizationBalanceSettingsResult.fertilizationBalanceSettings
        .filter((balanceSetting) => fertilizationSettingsByAdviceId.has(balanceSetting.adviceId))
        .map((balanceSetting) => {
          const correspondingFertilizationSetting = fertilizationSettingsByAdviceId.get(balanceSetting.adviceId);
          return {
            ...balanceSetting,
            expectedCropYieldProduct: correspondingFertilizationSetting?.expectedCropYield ?? 0,
          };
        });

    return new OpenEditFertilizationBalanceSettingsDialog(
      mergedFertilizationBalanceSettings,
      fertilizationBalanceSettingsResult.headerBusinessRuleViolations,
    );
  }
}
